import React, { SyntheticEvent, useCallback, useEffect, useMemo, useState } from "react";

import {
  Button,
  Checkbox,
  Dropdown,
  Header,
  Image,
  Input,
  Modal,
  Popup,
  Tab,
  TabProps,
} from "semantic-ui-react";

import "react-table-6/react-table.css";

import { Table } from "react-lib/frameworks/Table";

import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";
import DropdownOptions from "react-lib/appcon/common/DropdownOptions";

import { mapOptions } from "react-lib/apps/HISV3/common/CommonInterface";

import ModConfirmOA from "react-lib/apps/IsHealth/REG/ModConfirmOA";
import {
  ADMIN_NAME_LIST,
  ADMIN_TAB_LIST,
  MOBILE_MENU_LIST,
} from "react-lib/apps/USER/CardSettingDivision";

import SnackMessage from "../common/SnackMessage";
import ModInfoOA from "../IsHealth/REG/ModInfoOA";
import UserProfile from "../IsHealth/REG/UserProfile";

import ModApproveInviteSuccess from "./ModApproveInviteSuccess";
import ModInviteLink from "./ModInviteLink";

import { ADMIN_MENU_OPTIONS } from "../HISV3/HRM/sequence/DSPermission";
import {
  ACTIONS,
  BTN_ACTS,
  MasterOptions,
  OAInvitationType,
  PickedState,
  ProfiledDivisionHasUserType,
  RunSequence,
  SEQUENCE_NAME,
  SetProp,
  State,
  STATUS_OPTIONS,
} from "./sequence/PermissionManagement";

import { formatDatetime } from "react-lib/utils/dateUtils";

import "./CardPermissionManagement.scss";

import CONFIG from "config/config";

type CardPermissionManagementProps = Pick<
  PickedState,
  "buttonLoadCheck" | "errorMessage" | "selectedDivision"
> & {
  setProp: SetProp;
  proxyController: any;
  runSequence: RunSequence;
  PermissionManagementSequence: State["PermissionManagementSequence"];
  masterOptions: MasterOptions;
};

const PROFILE_IMAGE_URL = `${process.env.PUBLIC_URL}/static/images/person-placeholder.png`;

const CardPermissionManagement = (props: CardPermissionManagementProps) => {
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [modDeleteRequest, setModDeleteRequest] = useState<OAInvitationType | null>(null);
  const [modDeleteMember, setModDeleteMember] = useState<ProfiledDivisionHasUserType | null>(null);
  const [modSuccess, setModSuccess] = useState<OAInvitationType | null>(null);
  const [modInfoMsg, setModInfoMsg] = useState("");

  useEffect(() => {
    props.runSequence({ sequence: "PermissionManagement", restart: true });
  }, [props.selectedDivision?.id]);

  useEffect(() => {
    const message = props.buttonLoadCheck?.[BTN_ACTS.SAVE_MEMBER];

    if (message && message !== "LOADING") {
      setModInfoMsg(message === "SUCCESS" ? "บันทึกสำเร็จ" : "เกิดข้อผิดพลาด");
    }
  }, [props.buttonLoadCheck]);

  const positionOption = useMemo(
    () => [
      { key: "ทั้งหมด", text: "ทั้งหมด", value: "ทั้งหมด" },
      ...(props.masterOptions.positionAll ?? []),
    ],
    [props.masterOptions.positionAll]
  );

  const handleOpenProfile = useCallback((data: OAInvitationType) => {
    props.runSequence({
      sequence: "PermissionManagement",
      action: ACTIONS.GET_PROFILE,
      id: data.user,
      image: data.user_profile?.image,
      user_profile: data.user_profile,
    });
  }, []);

  const handleApprove = (data: OAInvitationType) => {
    if (data.id) {
      props.runSequence({
        sequence: "PermissionManagement",
        action: ACTIONS.APPROVE_INVITE,
        id: data.id,
        onSuccess: () => {
          setModSuccess(data);
        },
      });
    }
  };

  const handleSaveMember = () => {
    props.runSequence({ sequence: "PermissionManagement", action: ACTIONS.SAVE_MEMBER });
  };

  const handleChangeValue = (index: number) => (e: SyntheticEvent, data: any) => {
    const items: any[] = props.PermissionManagementSequence?.oaUserList ?? [];
    const value = data.type === "checkbox" ? data.checked : data.value;

    items[index][data.name] = value;
    items[index].isEditing = true;

    props.setProp("PermissionManagementSequence.oaUserList", [...items]);
  };

  const filteredAdminMenuList = useMemo(() => {
    const filtered = ADMIN_MENU_OPTIONS.filter(
      (option) => !CONFIG.V3_HIDDEN_TAB_LIST.includes(option.value)
    );

    return filtered.map((item) =>
      item.key === ADMIN_NAME_LIST.ข้อมูลบุคลากร && "subOptions" in item
        ? {
            ...item,
            subOptions: item.subOptions.filter((acc) =>
              [
                ADMIN_TAB_LIST["Employee Document"],
                ADMIN_TAB_LIST["Employee info history"],
                ADMIN_TAB_LIST["Employee Information"],
              ].includes(acc.key as any)
            ),
          }
        : item
    );
  }, []);

  const formattedOaUserList = useMemo(() => {
    const items = props.PermissionManagementSequence?.oaUserList ?? [];

    return items.map((item, index) => {
      const foundPosition = (props.masterOptions.positionAll ?? []).find(
        (option) => Number(option.value) === item.position
      );
      const mobileMenuList = mapOptions(Object.keys(MOBILE_MENU_LIST));

      return {
        ...item,
        active: (
          <div style={{ display: "grid", placeContent: "center" }}>
            <Checkbox
              checked={item.is_Active}
              name="is_Active"
              onChange={handleChangeValue(index)}
            />
          </div>
        ),
        adminMenu: (
          <DropdownOptions
            menuStyle={{ maxHeight: "calc(100vh - 37.75rem)", paddingBottom: "4rem" }}
            name="adminMenuList"
            value={item.adminMenuList}
            checked
            clearable
            multiple
            search
            searchBox
            options={filteredAdminMenuList}
            showSubOptions
            onChange={handleChangeValue(index)}
          />
        ),
        delete: (
          <div style={{ display: "grid", placeContent: "center" }}>
            <Button
              style={{ fontSize: "0.65rem" }}
              color="red"
              icon="minus"
              size="mini"
              circular
              onClick={() => {
                setModDeleteMember(item);
              }}
            />
          </div>
        ),
        isDoctor: (
          <div style={{ display: "grid", placeContent: "center" }}>
            <Checkbox
              checked={item.is_doctor}
              name="is_doctor"
              onChange={handleChangeValue(index)}
            />
          </div>
        ),
        mobileMenu: (
          <DropdownOptions
            name="mobileMenuList"
            value={item.mobileMenuList}
            checked
            clearable
            multiple
            search
            searchBox
            options={mobileMenuList}
            showSubOptions
            onChange={handleChangeValue(index)}
          />
        ),
        position: foundPosition?.text && (
          <DropdownOptions
            style={{ width: "100%" }}
            name="position"
            value={item.position}
            fluid
            search
            options={props.masterOptions.positionAll}
            onChange={handleChangeValue(index)}
          />
        ),
      };
    });
  }, [
    filteredAdminMenuList,
    props.masterOptions.positionAll,
    props.PermissionManagementSequence?.oaUserList,
  ]);

  const formattedOaRequestList = useMemo(() => {
    const items = props.PermissionManagementSequence?.oaRequestList ?? [];

    return items.map((item) => ({
      ...item,
      approve: (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <Button
            className="pink"
            loading={props.buttonLoadCheck?.[`${BTN_ACTS.APPROVE_INVITE}_${item.id}`]}
            size="mini"
            basic
            onClick={() => {
              handleApprove(item);
            }}
          >
            อนุมัติคำขอ
          </Button>
        </div>
      ),
      created: formatDatetime(item.created, true),
      delete: (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <Button
            className="red"
            size="mini"
            basic
            onClick={() => {
              setModDeleteRequest(item);
            }}
          >
            ลบคำขอ
          </Button>
        </div>
      ),
      fullname: (
        <div style={{ alignItems: "center", display: "flex" }}>
          <Image
            style={{ height: "35px", objectFit: "cover", width: "35px" }}
            src={item.user_profile?.image || PROFILE_IMAGE_URL}
            circular
          />
          <label style={{ marginLeft: "1rem" }}>{item.user_profile?.fullname}</label>
          <Popup
            style={{ border: "1px solid var(--primary-theme-color)" }}
            content="คลิกเพื่อดูโปรไฟล์"
            position="bottom left"
            trigger={
              <Button
                style={{
                  backgroundColor: "var(--primary-theme-color)",
                  borderRadius: "12px",
                  color: "white",
                  fonSize: "0.8rem",
                  marginLeft: "1rem",
                  padding: "0.475rem 1rem",
                }}
                loading={props.buttonLoadCheck?.[`${BTN_ACTS.GET_PROFILE}_${item.user}`]}
                size="mini"
                onClick={() => {
                  handleOpenProfile(item);
                }}
              >
                ข้อมูลส่วนตัว
              </Button>
            }
          />
        </div>
      ),
    }));
  }, [props.buttonLoadCheck, props.PermissionManagementSequence?.oaRequestList]);

  const panes = useMemo(
    () => [
      {
        menuItem: {
          key: "pending",
          content: (
            <div style={{ alignItems: "center", display: "flex", gap: "8px" }}>
              <span>คำร้องขอ</span>
              <div className="badge">{formattedOaRequestList.length}</div>
            </div>
          ),
        },
        render: () => (
          <Tab.Pane>
            <div>
              <Table
                data={formattedOaRequestList}
                headers="ชื่อ,Request Date,Approve,Delete"
                keys="fullname,created,approve,delete"
                minRows={9}
                showPagination={false}
                widths="^130,^100,^100,^100"
              />
            </div>
          </Tab.Pane>
        ),
      },
      {
        menuItem: {
          key: "approved",
          content: (
            <div style={{ alignItems: "center", display: "flex", gap: "8px" }}>
              <span>สมาชิก</span>
              <div className="badge">{formattedOaUserList.length}</div>
            </div>
          ),
        },
        render: () => (
          <Tab.Pane>
            <div>
              <div
                style={{
                  alignItems: "center",
                  display: "flex",
                  marginBottom: "20px",
                  padding: "0 15px",
                }}
              >
                <div
                  style={{
                    alignItems: "center",
                    display: "flex",
                  }}
                >
                  <span style={{ marginRight: "10px" }}>ชื่อ-นามสกุล ผู้ให้บริการ</span>
                  <Input
                    style={{ minWidth: "300px" }}
                    icon={{ circular: false, link: false, name: "search" }}
                    name="filterOAuser.name"
                    placeholder="ค้นหาด้วยชื่อ หรือ นามสกุล"
                    value={props.PermissionManagementSequence?.filterOAuser?.name}
                    onChange={handleChangeData}
                  />
                </div>
                <div
                  style={{
                    alignItems: "center",
                    display: "flex",
                    zIndex: "999",
                  }}
                >
                  <span style={{ margin: "0px 10px" }}>ตำแหน่ง</span>
                  <Dropdown
                    name="filterOAuser.position"
                    value={props.PermissionManagementSequence?.filterOAuser?.position}
                    clearable
                    selection
                    options={positionOption}
                    onChange={handleChangeData}
                  />
                </div>
                <div
                  style={{
                    alignItems: "center",
                    display: "flex",
                    zIndex: "999",
                  }}
                >
                  <span style={{ margin: "0px 10px" }}>สถานะ</span>
                  <Dropdown
                    name="filterOAuser.status"
                    value={props.PermissionManagementSequence?.filterOAuser?.status}
                    clearable
                    selection
                    options={STATUS_OPTIONS}
                    onChange={handleChangeData}
                  />
                </div>
                <div style={{ margin: "0px 10px" }}>
                  <ButtonLoadCheck
                    setProp={props.setProp}
                    style={{ backgroundColor: "var(--primary-theme-color)", color: "white" }}
                    paramKey={BTN_ACTS.SEARCH_MEMBER}
                    size="medium"
                    title="ค้นหา"
                    buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.SEARCH_MEMBER]}
                    onClick={handleSearchMember}
                  />
                </div>
              </div>

              <Table
                className="oa-user-list"
                data={formattedOaUserList}
                headers="ชื่อ,ตำแหน่ง,เมนู Admin Desktop,เมนู Admin Mobile,เป็นผู้ให้บริการ,Active,ลบสมาชิก"
                keys="fullname,position,adminMenu,mobileMenu,isDoctor,active,delete"
                minRows={9}
                showPagination={false}
                widths="^85,^80,^300,^180,120,80,100"
              />
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  marginTop: "1rem",
                  paddingBottom: "4rem",
                }}
              >
                <ButtonLoadCheck
                  setProp={props.setProp}
                  style={{ backgroundColor: "var(--primary-theme-color)", color: "white" }}
                  paramKey={BTN_ACTS.SAVE_MEMBER}
                  size="medium"
                  title="บันทึก"
                  buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.SAVE_MEMBER]}
                  onClick={handleSaveMember}
                />
              </div>
            </div>
          </Tab.Pane>
        ),
      },
    ],
    [formattedOaRequestList, formattedOaUserList]
  );

  const handleInviteMember = () => {
    props.runSequence({ sequence: "PermissionManagement", action: ACTIONS.INVITE_MEMBER });
  };

  const handleCloseModInvite = () => {
    props.setProp("PermissionManagementSequence.inviteLink", "");
  };

  const handleCloseModConfirm = () => {
    setModDeleteRequest(null);
    setModDeleteMember(null);
  };

  const handleConfirmDeleteRequest = () => {
    if (modDeleteRequest?.id) {
      props.runSequence({
        sequence: "PermissionManagement",
        action: ACTIONS.DELETE_INVITE,
        id: modDeleteRequest.id,
        onSuccess: handleCloseModConfirm,
      });
    }
  };

  const handleConfirmDeleteMember = () => {
    if (modDeleteMember?.id) {
      props.runSequence({
        sequence: "PermissionManagement",
        action: ACTIONS.DELETE_MEMBER,
        id: modDeleteMember.id,
        onSuccess: handleCloseModConfirm,
      });
    }
  };

  const handleTabChange = (e: SyntheticEvent, data: TabProps) => {
    setActiveTabIndex(data.activeIndex as number);

    props.runSequence({
      sequence: "PermissionManagement",
      action: data.activeIndex === 0 ? ACTIONS.SEARCH_REQUEST : ACTIONS.SEARCH_MEMBER,
    });
  };

  const handleCloseModProfile = () => {
    props.setProp("PermissionManagementSequence.userProfile", null);
  };

  const handleChangeData = (e: SyntheticEvent, v: any) => {
    const value = v.type === "checkbox" ? v.checked : v.value;

    props.setProp(`PermissionManagementSequence.${v.name}` as any, value || null);
  };

  const handleSearchMember = () => {
    props.runSequence({
      sequence: "PermissionManagement",
      action: ACTIONS.SEARCH_MEMBER,
    });
  };

  const handleCloseModInfo = () => {
    setModInfoMsg("");

    props.setProp(`buttonLoadCheck.${BTN_ACTS.SAVE_MEMBER}`, null);
  };

  return (
    <div className="card-permission-management" style={{ padding: "15px 0" }}>
      <SnackMessage
        error={props.errorMessage?.[SEQUENCE_NAME]}
        success={null}
        onClose={() => {
          props.setProp(`errorMessage.${SEQUENCE_NAME}`, null);
        }}
      />

      <div
        style={{
          alignItems: "center",
          display: "flex",
          justifyContent: "space-between",
          marginBottom: "20px",
          padding: "0 15px",
        }}
      >
        <Header style={{ color: "rgb(79, 79, 79)", margin: "0" }} as="h2">
          การจัดการสิทธิ์
        </Header>

        <ButtonLoadCheck
          setProp={props.setProp}
          style={{
            backgroundColor: "var(--primary-theme-color)",
            borderRadius: "5px",
            color: "white",
            position: "absolute",
            right: "1rem",
            top: "7.5rem",
          }}
          paramKey={BTN_ACTS.INVITE_MEMBER}
          size="medium"
          title="เพิ่มสมาชิก"
          buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.INVITE_MEMBER]}
          onClick={handleInviteMember}
        />
      </div>

      <Tab
        activeIndex={activeTabIndex}
        menu={{ pointing: true, secondary: true }}
        panes={panes}
        onTabChange={handleTabChange}
      />

      <ModInviteLink
        link={props.PermissionManagementSequence?.inviteLink || ""}
        open={!!props.PermissionManagementSequence?.inviteLink}
        onClose={handleCloseModInvite}
      />

      <ModConfirmOA
        approveButtonPosition="right"
        approveButtonText="ยืนยันลบ"
        denyButtonText="ยกเลิก"
        isLoading={!!props.buttonLoadCheck?.[BTN_ACTS.DELETE_INVITE]}
        open={!!modDeleteRequest}
        title="ยืนยันการลบคำขอ"
        onApprove={handleConfirmDeleteRequest}
        onClose={handleCloseModConfirm}
        onDeny={handleCloseModConfirm}
        subtitle={
          <>
            <span>คุณแน่ใจหรือไม่ว่าต้องการลบ</span>
            <span> {modDeleteRequest?.user_profile?.fullname}</span>
          </>
        }
      />

      <ModConfirmOA
        approveButtonPosition="right"
        approveButtonText="ยืนยันลบ"
        denyButtonText="ยกเลิก"
        isLoading={!!props.buttonLoadCheck?.[BTN_ACTS.DELETE_MEMBER]}
        open={!!modDeleteMember}
        title="ยืนยันการลบสมาชิก"
        onApprove={handleConfirmDeleteMember}
        onClose={handleCloseModConfirm}
        onDeny={handleCloseModConfirm}
        subtitle={
          <>
            <span>คุณแน่ใจหรือไม่ว่าต้องการลบ</span>
            <span> {modDeleteMember?.user_profile?.fullname}</span>
          </>
        }
      />

      <ModApproveInviteSuccess
        name={modSuccess?.user_profile?.fullname || ""}
        open={!!modSuccess}
        onClose={() => {
          setModSuccess(null);
        }}
      />

      <Modal
        className="card-permission-management__user-profile"
        style={{ minWidth: "81rem" }}
        open={!!props.PermissionManagementSequence?.userProfile}
        closeIcon
        closeOnDimmerClick
        onClose={handleCloseModProfile}
      >
        <UserProfile
          controller={props.proxyController}
          data={props.PermissionManagementSequence?.userProfile}
          layout="detailed"
          readOnly
        />
      </Modal>

      <ModInfoOA
        open={!!modInfoMsg}
        type={modInfoMsg.includes("บันทึกสำเร็จ") ? "success" : "error"}
        onApprove={handleCloseModInfo}
        onClose={handleCloseModInfo}
      />
    </div>
  );
};

CardPermissionManagement.displayName = "CardPermissionManagement";

export default React.memo(CardPermissionManagement);
